import { FC, FormEvent, useEffect, useRef, useState, useId } from 'react';
import { importAllIcons } from './ManageTaskDialog';
import { nameExists } from 'components/Client/Utility/candidates';
import { isUnresolvedCreate, submitIdempotentCreate } from 'config/idempotentCreate';
import { useTranslation } from 'react-i18next';
import { useClassifiedErrorMessages } from 'reusable/useSaveError';
import { inModalLayer, useModalDialog } from 'reusable/useModalDialog';

interface AddNewTaskDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * EV-129。作成できたかを確かめられなかったときに、作業の一覧を読み直す。同じ名前の作業があるかは
   * このダイアログがサーバーに聞く（EV-493）。
   */
  onReload?: () => Promise<unknown> | void;
}

export const TASKS_URL = '/v1/tasks/';

const iconsPerPage = 20;

const AddNewTaskDialog: FC<AddNewTaskDialogProps> = ({ isOpen, onClose, onReload }) => {
  const { t } = useTranslation('client');
  const [taskName, setTaskName] = useState<string>('');
  const [selectedIcon, setSelectedIcon] = useState<string>('');
  const [iconName, setIconName] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [icons, setIcons] = useState<{ [key: string]: string }>({});
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(true);
  // 追加に失敗した理由。以前は失敗してもダイアログが閉じ、理由も出なかった。
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const errorMessages = useClassifiedErrorMessages();
  // EV-129（ADM-14 の AC1）。送信中（送り直し・処理中の問い合わせ直しを含む）は、追加・名前・
  // アイコン・キャンセル・閉じるを押せない（RV-129・RV-130）。state の反映より前の二重の送信
  // （Enter の連打など）も止めるため、ref でも持つ。
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  // EV-461（#387 の RV-703 と同じ型）。作成の結果が分からないまま（前回の X-Request-ID を残した）なら、名前とアイコンを
  // 送った値のまま固定し、「同じ内容で送り直す」か、やめるだけにする。閉じたら解く。
  const [unresolved, setUnresolved] = useState(false);
  const unresolvedRef = useRef(false);
  const setLocked = (value: boolean) => {
    unresolvedRef.current = value;
    setUnresolved(value);
  };
  const noteId = useId();
  // RV-130。閉じた後に届いた結果では、表示を変えない（残す値の更新だけ行う）。
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // RV-028 と同じ。閉じたら前回の失敗の説明を消す（開き直したときに出さない）。
  useEffect(() => {
    if (!isOpen) {
      setSaveErrors([]);
      unresolvedRef.current = false;
      setUnresolved(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchIcons = async () => {
      const importedIcons = await importAllIcons();
      setIcons(importedIcons);
      const firstIconName = Object.keys(importedIcons)[0];
      setSelectedIcon(importedIcons[firstIconName]);
      setIconName(firstIconName);
    };

    fetchIcons();
  }, []);

  // ADM-16。Escape で閉じる・フォーカスを中に留める・閉じたら開いた操作へ戻す。
  // RV-125。見出しの id は文書の中で一意にする（重なったダイアログが別の見出しで読まれた）。
  const headingId = useId();
  // RV-130。送信中は Escape でも閉じない（閉じると結果を受け取れない。値は残る）。
  const closeUnlessSubmitting = () => {
    if (!submittingRef.current) onClose();
  };
  const dialogRef = useModalDialog(isOpen, closeUnlessSubmitting);

  if (!isOpen) return null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (submittingRef.current || unresolvedRef.current) return;
    const value = e.target.value.replace(/\s/g, '');
    setTaskName(value);
    setIsValid(!!e.target.value.trim());
  };

  const handleIconClick = (name: string) => {
    if (submittingRef.current || unresolvedRef.current) return;
    setSelectedIcon(icons[name]);
    setIconName(name);
  };

  // EV-129。作業の一覧を読み直し、同じ名前の作業があれば、作成済みでないかを確かめるよう
  // 添える。名前の一致は「作成できた」証拠にしない（前からある同じ名前の作業かもしれないため）。
  // EV-493。同じ名前は、一覧（以前は全件）から探さず、名前の完全一致でサーバーに1回だけ聞く。
  const messagesWithListCheck = async (lead: string, name: string): Promise<string[]> => {
    const messages = [lead];
    try {
      await onReload?.();
      if (await nameExists(TASKS_URL, name)) {
        messages.push(t('add_task.create-same-name-exists'));
      }
    } catch (error) {
      console.error('Failed to reload tasks', error);
    }
    return messages;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setIsTouched(true);

    if (!taskName.trim()) {
      setIsValid(false);
      return;
    }

    const payload = { name: taskName, icon: iconName };
    const iconSnapshot = selectedIcon;
    // RV-129。値を残すときは、画面を送った内容に戻す（送信中の変更は止めているが、念のため）。
    const restoreSubmitted = () => {
      setTaskName(payload.name);
      setIconName(payload.icon);
      setSelectedIcon(iconSnapshot);
    };
    submittingRef.current = true;
    setSubmitting(true);
    setSaveErrors([]);
    try {
      // EV-129（ADM-14 の AC1）。作成の操作ごとに X-Request-ID を作り、結果が分からない失敗では
      // 同じ値・同じ本文で送り直す。結果が分からなかった後に入力を変えずに押したら前回と同じ値
      // （24時間の期限・前回の値を残す条件・タブに残す・利用者の印も配送先の作成と共通。
      // config/idempotentCreate.ts）。
      const outcome = await submitIdempotentCreate('task', TASKS_URL, payload);
      if (mounted.current) setLocked(isUnresolvedCreate(outcome));
      if (outcome.status === 'created') {
        if (mounted.current) {
          setSaveErrors([]);
          onClose();
        }
      } else if (outcome.status === 'expired') {
        // RV-127。期限が近く、前回の値は使えない。一覧を読み直して警告する。
        const messages = await messagesWithListCheck(
          t('add_task.create-retry-expired'),
          payload.name
        );
        if (mounted.current) setSaveErrors(messages);
      } else if (outcome.status === 'unknown') {
        // 作成できたかを確かめられなかった（値は残した）。入力を残し、作業の一覧を読み直す。
        console.error('Error adding new task:', outcome.error);
        if (mounted.current) restoreSubmitted();
        const messages = await messagesWithListCheck(
          t('add_task.create-outcome-unknown'),
          payload.name
        );
        if (mounted.current) setSaveErrors(messages);
      } else {
        console.error('Error adding new task:', outcome.error);
        if (outcome.keptRecord && mounted.current) restoreSubmitted();
        if (mounted.current) setSaveErrors(errorMessages(outcome.error));
      }
    } finally {
      submittingRef.current = false;
      if (mounted.current) setSubmitting(false);
    }
  };

  const handleNextPage = () => {
    if (currentPage < Math.ceil(Object.keys(icons).length / iconsPerPage)) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const startIndex = (currentPage - 1) * iconsPerPage;
  const currentIcons = Object.entries(icons).slice(
    startIndex,
    startIndex + iconsPerPage
  );

  return inModalLayer(
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div
          className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 w-[804px]"
          role="dialog"
          ref={dialogRef}
          aria-modal="true"
          aria-labelledby={headingId}
          tabIndex={-1}
        >
          <div className="bg-white">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-left sm:mt-0 w-full">
                <div className="flex justify-between items-center mt-4 mx-6">
                  <h3
                    className="text-subtitle-1 font-medium text-neutral/300"
                    id={headingId}
                  >
                    {t('add_task.title')}
                  </h3>
                  <button type="button"
                    onClick={closeUnlessSubmitting}
                    disabled={submitting}
                    className="text-gray-600 hover:text-gray-800 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">{t('ui.close', { ns: 'common' })}</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <hr className="border-gray-300 mt-4 w-full" />
                <form onSubmit={handleSubmit}>
                  {/* RV-129。送信中は名前・アイコン・キャンセル・追加をまとめて無効にする。 */}
                  <fieldset disabled={submitting} className="min-w-0">
                  {/* EV-461。結果が分からない間は、名前とアイコンを送った値のまま固定する（キャンセルと送り直しは押せる）。 */}
                  <fieldset disabled={unresolved} className="min-w-0" aria-describedby={unresolved ? noteId : undefined}>
                  <div className="grid grid-cols-4 gap-x-4 gap-y-4 p-6 h-56">
                    <div className="h-40 flex-col col-span-1 justify-start items-center gap-2 inline-flex">
                      <div className="p-4 bg-white/20 rounded-lg border-2 border-[#999996] justify-center items-center">
                        {/* EV-461 の画面の axe（image-alt）。選んだアイコンの見本。アイコンの名前は下のボタンが持つので飾り。 */}
                        <img src={selectedIcon} alt="" className="w-20 h-20" />
                      </div>
                      <div className="gap-y-2">
                        <input
                          type="text"
                          maxLength={10}
                          placeholder={t('add_task.placeholder')}
                          value={taskName}
                          onChange={handleInputChange}
                          className={`w-full h-10 p-4 text-center text-body-2 border rounded-md focus:outline-none
    ${!isValid && isTouched ? 'border-error' : 'border-neutral/50'}
    ${
      !isValid && isTouched ? 'focus:border-error' : 'focus:border-neutral/75'
    }`}
                        />
                        {!isValid && (
                          <div className="text-red-500 text-overline w-full text-start whitespace-nowrap">
                            {t('add_task.error')}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="w-full col-span-3">
                      <div className="grid grid-cols-10 gap-2">
                        {currentIcons.map(([name, path], index) => (
                          <button
                            type="button"
                            key={index}
                            onClick={() => handleIconClick(name)}
                            aria-pressed={selectedIcon === path}
                            className={`flex justify-center items-center w-12 h-12 cursor-pointer rounded-md ${
                              selectedIcon === path ? 'bg-primary/50' : 'border'
                            }`}
                          >
                            <img
                              src={path}
                              alt={`icon-${name}`}
                              className="w-8 h-8"
                            />
                          </button>
                        ))}
                      </div>
                      <div className="flex justify-end space-x-3 mt-4">
                        <button
                          type="button"
                          className={`w-20 h-8 border border-neutral/50 text-neutral/300 rounded-md ${
                            currentPage === 1
                              ? 'cursor-not-allowed opacity-50 text-neutral/75'
                              : ''
                          }`}
                          onClick={handlePrevPage}
                          disabled={currentPage === 1}
                        >
                          &lt; {t('action.back')}
                        </button>
                        <button
                          type="button"
                          className={`w-20 h-8 border border-neutral/50 text-neutral/300 rounded-md ${
                            currentPage ===
                            Math.ceil(Object.keys(icons).length / iconsPerPage)
                              ? 'cursor-not-allowed opacity-50'
                              : ''
                          }`}
                          onClick={handleNextPage}
                          disabled={
                            currentPage ===
                            Math.ceil(Object.keys(icons).length / iconsPerPage)
                          }
                        >
                          {t('action.next')} &gt;
                        </button>
                      </div>
                    </div>
                  </div>
                  </fieldset>
                  {unresolved ? (
                    <p id={noteId} className="px-4 pb-2 text-body-2 text-neutral/300">
                      {t('operator.unresolved-note', { ns: 'layout' })}
                    </p>
                  ) : null}
                  {saveErrors.length > 0 && (
                    <div className="px-4 pb-2 text-error text-body-2" role="alert">
                      {saveErrors.map((message) => (
                        <p key={message}>{message}</p>
                      ))}
                    </div>
                  )}
                  <div className="px-4 py-3 border-t-[1px] grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center items-center rounded-md border bg-primary/50 text-subtitle-2 text-primary/300 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={closeUnlessSubmitting}
                    >
                      {t('action.cancel')}
                    </button>
                    <button
                      type="submit"
                      aria-busy={submitting}
                      className="w-full inline-flex justify-center items-center rounded-md bg-primary/300 text-subtitle-2 text-white h-12 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {unresolved ? t('operator.resend-same', { ns: 'layout' }) : t('action.add_task')}
                    </button>
                  </div>
                  </fieldset>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNewTaskDialog;
